import { useState, useLayoutEffect, useRef, useCallback } from 'react';

export interface NavItem {
  label: string;
  href: string;
}

interface UseNavOverflowOptions {
  gap?: number;              // horizontal gap between items (px)
  reserveMoreMin?: number;   // minimum width to reserve for the more button
  rtl?: boolean;             // for potential directional adjustments
  debounce?: number;         // resize debounce ms
}

interface UseNavOverflowResult {
  containerRef: React.RefObject<HTMLDivElement>;
  registerMeasureEl: (index: number) => (el: HTMLElement | null) => void;
  moreRef: React.RefObject<HTMLSpanElement>;
  visible: NavItem[];
  hidden: NavItem[];
  recompute: () => void;
}

/**
 * Generic navigation overflow measurement hook.
 * Pure layout math; caller supplies ordered items.
 */
export function useNavOverflow(items: NavItem[], opts: UseNavOverflowOptions = {}): UseNavOverflowResult {
  const { gap = 8, reserveMoreMin = 40, debounce = 80 } = opts;
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRefs = useRef<(HTMLElement | null)[]>([]);
  const moreRef = useRef<HTMLSpanElement>(null);

  const [visible, setVisible] = useState<NavItem[]>(items);
  const [hidden, setHidden] = useState<NavItem[]>([]);

  // keep refs array length synced
  if (measureRefs.current.length !== items.length) {
    measureRefs.current = new Array(items.length).fill(null);
  }

  const calculate = useCallback(() => {
    const container = containerRef.current;
    if (!container || items.length === 0) {
      setVisible(items);
      setHidden([]);
      return;
    }
    const containerWidth = container.offsetWidth;
    if (containerWidth <= 0) {
      setVisible(items);
      setHidden([]);
      return;
    }

    // collect widths
    const widths = items.map((_, i) => {
      const el = measureRefs.current[i];
      return el?.offsetWidth || 72; // fallback
    });
    const moreWidth = moreRef.current?.offsetWidth || reserveMoreMin;

    // check if all fit
    const totalGaps = gap * Math.max(0, items.length - 1);
    const totalWidthAll = widths.reduce((a, b) => a + b, 0) + totalGaps;
    if (totalWidthAll <= containerWidth) {
      setVisible(items);
      setHidden([]);
      return;
    }

    let used = 0;
    let cutoff = items.length;
    for (let i = 0; i < items.length; i++) {
      const w = widths[i];
      const hasMoreAfter = (i + 1) < items.length;
      const reserve = hasMoreAfter ? moreWidth + gap : 0; // keep space for more
      const withGap = used === 0 ? w : w + gap;
      if (used + withGap + reserve <= containerWidth) {
        used += withGap;
      } else {
        cutoff = i;
        break;
      }
    }

    if (cutoff >= items.length) {
      setVisible(items);
      setHidden([]);
    } else {
      const visCount = Math.max(1, cutoff); // ensure at least one visible
      setVisible(items.slice(0, visCount));
      setHidden(items.slice(visCount));
    }
  }, [items, gap, reserveMoreMin]);

  // debounce resize / item changes
  useLayoutEffect(() => {
    let raf: number | null = null;
    let timeout: number | null = null;
    const trigger = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => calculate());
    };
    trigger();

    const handleResize = () => {
      if (timeout) window.clearTimeout(timeout);
      timeout = window.setTimeout(trigger, debounce);
    };

    window.addEventListener('resize', handleResize);
    const ro = containerRef.current ? new ResizeObserver(handleResize) : null;
    if (containerRef.current) ro?.observe(containerRef.current);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (timeout) window.clearTimeout(timeout);
      window.removeEventListener('resize', handleResize);
      ro?.disconnect();
    };
  }, [calculate, debounce]);

  const registerMeasureEl = (index: number) => (el: HTMLElement | null) => {
    measureRefs.current[index] = el;
  };

  return { containerRef, registerMeasureEl, moreRef, visible, hidden, recompute: calculate };
}

export default useNavOverflow;
